import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FieldProps, Form, getIn } from 'formik'
import { Field } from 'formik'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  VStack,
  Select,
} from '@chakra-ui/react'
import { colors, IKReactSelectCreatable } from '../../../../ui-kit'
import { Trans, useTranslation } from 'react-i18next'
import CMUploadForm from '../Details/CustomMetadataForm/CMUploadForm'
import {
  DEFAULT_CLIENT_TAG_LIMIT,
  DEFAULT_CLIENT_TAG_SKIP,
} from '../../../constants'
import RemoveBgModal from '../Details/RemoveBgModal'
import AutoTagsModal from '../Details/AutoTagsModal'
import { UserContext } from '../../../providers'
import { useDebounce } from '../../../hooks/useDebounce'
import {
  ClientTagRequestResponse,
  ClientTagResponse,
} from '../../../interfaces'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../hooks/useToast'
import { formatTags } from '../../../../utils/mediaLibrary'
import { checkCfRequired, fileConstraints } from './uploadUtils'
import { BsInfoCircleFill } from 'react-icons/bs'
import { FeaturesEnum } from '../../../../utils/features'
import PostTransformationFields from './PostTransformationFields'
import { NavLink } from 'react-router-dom'
import SavedExtensionsSection from './SavedExtensionsSection'

interface ExtensionData {
  removeBg?: boolean
  aiTag?: boolean
  autoDescription?: boolean
}

const MetaDataFields = ({
  file,
  handleChange,
  values,
  isCommonSettings,
  cmFields,
  fileVersion,
  pathPolicy,
}) => {
  const { t } = useTranslation()
  const [isModalOpen, ToggleModal] = useState<boolean>(false)
  const [isAutoTagsModalOpen, toggleAutoTagsModal] = useState(false)
  const { user } = useContext(UserContext)
  const [search, setSearch] = useState<string>()
  const debouncedSearchTerm = useDebounce(search, 500)
  const [suggestedTags, setSuggestedTags] = useState<ClientTagResponse[]>([])
  const [disableCmSelect, setDisableCmSelect] = useState(!values?.overwriteCustomMetadata || Boolean(pathPolicy));
  const [showCmForm, setShowCmForm] = useState(values?.customMetadataSelect === "cm_form" || Boolean(pathPolicy));
  const { request } = useFetch<ClientTagRequestResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()

  useEffect(()=>{
    setShowCmForm(pathPolicy ? true : values?.customMetadataSelect === 'cm_form')
    setDisableCmSelect(pathPolicy ? true : !values?.overwriteCustomMetadata)
  },[values?.customMetadataSelect,values?.overwriteCustomMetadata, pathPolicy])
  const getSuggestedTags = useCallback(
    async debouncedSearchTerm => {
      if (user?.clientNumber) {
        let url = `/api/clients/${user?.clientNumber}/client-tags?limit=${DEFAULT_CLIENT_TAG_LIMIT}&skip=${DEFAULT_CLIENT_TAG_SKIP}`
        if (debouncedSearchTerm) url = `${url}&label=${debouncedSearchTerm}`

        const response = await request.get(url)
        if (request.error) {
          toast({
            description: t(
              'error_fetching_data',
              'Error in fetching data. Try refreshing the page.',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else if (response?.tagsData && Array.isArray(response.tagsData)) {
          setSuggestedTags(response.tagsData)
        }
      }
    },
    [user],
  )

  const tagsOptions = useMemo(() => {
    return suggestedTags.map(data => ({
      label: data.label,
      value: data.name,
    }))
  }, [suggestedTags])

  useEffect(() => {
    if (user) getSuggestedTags(debouncedSearchTerm)
  }, [debouncedSearchTerm, user])

  useEffect(() => {
    setDisableCmSelect(values?.overwriteCustomMetadata ? false : true)
  }, [values?.overwriteCustomMetadata])

  const handleTagChange = useCallback(options => {
    const updatedOptions = formatTags(options)
    handleChange({
      target: {
        value: updatedOptions,
        name: 'tags',
      },
    })
  }, [])


  return (
    <VStack alignItems="flex-start" width="100%" spacing={'2em'} as={Form}>
      <HStack
        display={'flex'}
        justifyContent="space-between"
        alignItems={'flex-start'}
        width="100%"
      >
        <VStack
          spacing="2em"
          direction="column"
          alignItems="flex-start"
          width="60%"
        >
          <Alert status="info">
            <AlertIcon
              height="1em"
              width="1em"
              alignItems="flex-start"
              marginTop="4px"
            />
            <AlertDescription fontSize="sm">
              {pathPolicy
                ? t('path_policy_edit_metadata_info')
                : !isCommonSettings
                ? t('edit_metadata_info')
                : t('common_settings_info')}
            </AlertDescription>
          </Alert>

          {!isCommonSettings && (
            <Field name="name">
              {({ field, form }: FieldProps<string, { name: string }>) => (
                <FormControl
                  isInvalid={Boolean(form.errors.name && form.touched.name)}
                  isRequired
                >
                  <FormLabel htmlFor="name" fontWeight={700}>
                    {t('file_name', 'File name')}
                  </FormLabel>

                  <Input
                    {...field}
                    id="name"
                    placeholder="File name"
                    value={values?.name}
                    marginTop="5px"
                    onChange={event => {
                      handleChange(event)
                    }}
                  />
                  <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          )}
          <VStack width={'100%'} alignItems="flex-start">
            <FormLabel htmlFor="name" fontWeight={700}>
              {t('tags', 'Tags')}
            </FormLabel>

            <IKReactSelectCreatable
              isMulti
              width="100%"
              placeholder="Select tags"
              value={
                typeof values?.tags === 'string' && values?.tags?.length
                  ? values?.tags
                      ?.split(',')
                      .map(data => ({ label: data, value: data }))
                  : values?.tags
              }
              onInputChange={value => setSearch(value)}
              options={tagsOptions}
              onChange={handleTagChange}
              isClearable={false}
            />
          </VStack>

          {isCommonSettings ||
          (!isCommonSettings &&
            file?.type &&
            fileConstraints.allowedFiles.image.includes(file.type)) ? (
            <>
              <VStack width={'100%'} alignItems="flex-start">
                <Field name="Exensions">
                  {({
                    form,
                  }: FieldProps<string, { extensionsData: ExtensionData }>) => (
                    <FormControl
                      isInvalid={Boolean(
                        form.errors.extensionsData &&
                          form.touched.extensionsData,
                      )}
                    >
                      <FormLabel htmlFor="extensionsData" fontWeight={700}>
                        {t('extensions', 'Extensions')}
                      </FormLabel>

                      <Field name="extensionsData.removeBg">
                        {({
                          form,
                        }: FieldProps<
                          string,
                          { 'extensionsData.removeBg': string }
                        >) => (
                          <FormControl
                            marginTop="1em"
                            isInvalid={Boolean(
                              form.errors['extensionsData.removeBg'] &&
                                form.touched['extensionsData.removeBg'],
                            )}
                          >
                            <HStack>
                              <Checkbox
                                isChecked={values?.extensionsData?.removeBg}
                                name="extensionsData.removeBg"
                                onChange={event => {
                                  handleChange(event)
                                  if (event.target.checked)
                                    handleChange({
                                      target: {
                                        value: {
                                          background: 'no_background',
                                          bgColor: '#0450d5',
                                          bgImageURL: '',
                                          semiTransparentCheck: false,
                                          shadowCheck: false,
                                        },
                                        name: 'removeBgData',
                                      },
                                    })
                                }}
                              >
                                {t('remove_background', 'Remove Background')}
                              </Checkbox>
                              {values?.extensionsData?.removeBg && (
                                <Button
                                  onClick={() => {
                                    ToggleModal(true)
                                  }}
                                  variant="link"
                                  color={colors.appBlue}
                                >
                                  {t('configure', 'Configure')}
                                </Button>
                              )}
                            </HStack>
                            <FormErrorMessage>
                              {form.errors['extensionsData.removeBg']}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="extensionsData.aiTag">
                        {({
                          form,
                        }: FieldProps<
                          string,
                          { 'extensionsData.aiTag': string }
                        >) => (
                          <FormControl
                            marginTop="1em"
                            isInvalid={Boolean(
                              form.errors['extensionsData.aiTag'] &&
                                form.touched['extensionsData.aiTag'],
                            )}
                          >
                            <HStack>
                              <Checkbox
                                isChecked={values?.extensionsData?.aiTag}
                                name="extensionsData.aiTag"
                                onChange={event => {
                                  handleChange(event)
                                  if (event.target.checked)
                                    handleChange({
                                      target: {
                                        value: [
                                          {
                                            maxTags: 5,
                                            minConfidence: 70,
                                            name: 'aws-auto-tagging',
                                          },
                                          {
                                            maxTags: 5,
                                            minConfidence: 70,
                                            name: 'google-auto-tagging',
                                          },
                                        ],
                                        name: 'aiTagData',
                                      },
                                    })
                                }}
                              >
                                {t('add_auto_tags', 'Add auto tags')}
                              </Checkbox>
                              {values?.extensionsData?.aiTag && (
                                <Button
                                  onClick={() => {
                                    toggleAutoTagsModal(true)
                                  }}
                                  variant="link"
                                  color={colors.appBlue}
                                >
                                  {t('configure', 'Configure')}
                                </Button>
                              )}
                            </HStack>
                            <FormErrorMessage>
                              {form.errors['extensionsData.aiTag']}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="extensionsData.autoDescription">
                        {({
                          form,
                        }: FieldProps<
                          string,
                          { 'extensionsData.autoDescription': string }
                        >) => (
                          <FormControl
                            marginTop="1em"
                            isInvalid={Boolean(
                              form.errors['extensionsData.autoDescription'] &&
                                form.touched['extensionsData.autoDescription'],
                            )}
                          >
                            <HStack>
                              <Checkbox
                                isChecked={
                                  values?.extensionsData?.autoDescription
                                }
                                name="extensionsData.autoDescription"
                                onChange={handleChange}
                              >
                                {t(
                                  'add_auto_description',
                                  'Add auto description',
                                )}
                              </Checkbox>
                            </HStack>
                            <FormErrorMessage>
                              {form.errors['extensionsData.autoDescription']}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <FormErrorMessage>
                        {form.errors.extensionsData}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </VStack>

              <RemoveBgModal
                isOpen={isModalOpen}
                onClose={() => {
                  ToggleModal(false)
                }}
                removeBgData={values?.removeBgData}
                setRemoveBgData={removeBgData =>
                  handleChange({
                    target: {
                      value: removeBgData,
                      name: 'removeBgData',
                    },
                  })
                }
                multipleFile={isCommonSettings}
              />
              <AutoTagsModal
                isOpen={isAutoTagsModalOpen}
                onClose={() => {
                  toggleAutoTagsModal(false)
                }}
                aiTagData={values?.aiTagData}
                setAiTagData={aiTagData => {
                  handleChange({
                    target: {
                      value: aiTagData,
                      name: 'aiTagData',
                    },
                  })
                  if (!aiTagData?.length) {
                    handleChange({
                      target: {
                        value: false,
                        name: 'extensionsData.aiTag',
                      },
                    })
                  }
                }}
                multipleFile={isCommonSettings}
              />
            </>
          ) : (
            <></>
          )}

          <SavedExtensionsSection
            values={values}
            handleChange={handleChange}
            form={{ errors: {}, touched: {} }}
            isDisabled={false}
            fieldPath="savedExtensions"
          />

          {!isCommonSettings && (
            <Field name="folder">
              {({ field, form }: FieldProps<string, { folder: string }>) => (
                <FormControl
                  marginTop="1em"
                  isInvalid={Boolean(form.errors.folder && form.touched.folder)}
                >
                  <FormLabel htmlFor="folder" fontWeight={700}>
                    {t('folder', 'Folder')}
                  </FormLabel>

                  <Input
                    {...field}
                    id="folder"
                    placeholder="folder"
                    value={values?.folder}
                    marginTop="5px"
                    onChange={handleChange}
                    disabled
                  />
                  <FormErrorMessage>{form.errors.folder}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          )}
        </VStack>
        {!isCommonSettings && (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent={'center'}
            width="35%"
            height="14rem"
            backgroundPosition="center"
            backgroundRepeat={'no-repeat'}
            backgroundImage={file?.preview}
            backgroundSize="contain"
          ></Box>
        )}
      </HStack>

      <VStack spacing="2em" alignItems="flex-start" width={'85%'}>
        {isCommonSettings ||
        (!isCommonSettings &&
          file?.type &&
          fileConstraints.allowedFiles.image.includes(file.type)) ? (
          <Field name="customCoordinates">
            {({
              form,
            }: FieldProps<string, { customCoordinatesOb: string }>) => (
              <FormControl
                isInvalid={Boolean(
                  form.errors.customCoordinatesOb &&
                    form.touched.customCoordinatesOb,
                )}
              >
                <FormLabel
                  htmlFor="customCoordinates"
                  marginBottom="1em"
                  fontWeight={700}
                >
                  {t('custom_focus_area', 'Custom focus area')}
                </FormLabel>

                <Stack display="flex" flexDirection="row">
                  <Field name="customCoordinatesOb.x-coordinate">
                    {({
                      field,
                      form,
                    }: FieldProps<
                      number,
                      { ['customCoordinatesOb.x-coordinate']: number }
                    >) => (
                      <FormControl
                        marginTop="0.5rem"
                        isInvalid={Boolean(
                          getIn(
                            form.errors,
                            'customCoordinatesOb.x-coordinate',
                          ) &&
                            getIn(
                              form.touched,
                              'customCoordinatesOb.x-coordinate',
                            ),
                        )}
                        isRequired={checkCfRequired(
                          values?.customCoordinatesOb,
                        )}
                      >
                        <FormLabel htmlFor="customCoordinatesOb.x-coordinate">
                          {t('x_coordinate', 'X coordinate')}
                        </FormLabel>

                        <Input
                          {...field}
                          id="customCoordinatesOb.x-coordinate"
                          name="customCoordinatesOb.x-coordinate"
                          placeholder="e.g. 10"
                          value={values?.customCoordinatesOb?.['x-coordinate']}
                          marginTop="5px"
                          onChange={event => {
                            handleChange({
                              target: {
                                value: {
                                  'x-coordinate': event?.target?.value,
                                  'y-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'y-coordinate'
                                    ],
                                  width: values?.customCoordinatesOb?.width,
                                  height: values?.customCoordinatesOb?.height,
                                },
                                name: 'customCoordinatesOb',
                              },
                            })
                          }}
                          width="90%"
                        />
                        <FormErrorMessage>
                          {getIn(
                            form.errors,
                            'customCoordinatesOb.x-coordinate',
                          )}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="customCoordinatesOb.y-coordinate">
                    {({
                      field,
                      form,
                    }: FieldProps<
                      string,
                      { ['customCoordinatesOb.y-coordinate']: number }
                    >) => (
                      <FormControl
                        isInvalid={Boolean(
                          getIn(
                            form.errors,
                            'customCoordinatesOb.y-coordinate',
                          ) &&
                            getIn(
                              form.touched,
                              'customCoordinatesOb.y-coordinate',
                            ),
                        )}
                        isRequired={checkCfRequired(
                          values?.customCoordinatesOb,
                        )}
                      >
                        <FormLabel htmlFor="customCoordinatesOb.y-coordinate">
                          {t('y_coordinate', 'Y coordinate')}
                        </FormLabel>

                        <Input
                          {...field}
                          id="customCoordinatesOb.y-coordinate"
                          name="customCoordinatesOb.y-coordinate"
                          placeholder="e.g. 10"
                          value={values?.customCoordinatesOb?.['y-coordinate']}
                          marginTop="5px"
                          onChange={event => {
                            handleChange({
                              target: {
                                value: {
                                  'x-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'x-coordinate'
                                    ],
                                  'y-coordinate': event?.target?.value,
                                  width: values?.customCoordinatesOb?.width,
                                  height: values?.customCoordinatesOb?.height,
                                },
                                name: 'customCoordinatesOb',
                              },
                            })
                          }}
                          width="90%"
                        />
                        <FormErrorMessage>
                          {getIn(
                            form.errors,
                            'customCoordinatesOb.y-coordinate',
                          )}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="customCoordinatesOb.height">
                    {({
                      field,
                      form,
                    }: FieldProps<
                      string,
                      { ['customCoordinatesOb.height']: number }
                    >) => (
                      <FormControl
                        isInvalid={Boolean(
                          getIn(form.errors, 'customCoordinatesOb.height') &&
                            getIn(form.touched, 'customCoordinatesOb.height'),
                        )}
                        isRequired={checkCfRequired(
                          values?.customCoordinatesOb,
                        )}
                      >
                        <FormLabel htmlFor="customCoordinatesOb.height">
                          {t('height', 'Height')}
                        </FormLabel>

                        <Input
                          {...field}
                          id="customCoordinatesOb.height"
                          name="customCoordinatesOb.height"
                          placeholder="e.g. 100"
                          value={values?.customCoordinatesOb?.['height']}
                          marginTop="5px"
                          onChange={event => {
                            handleChange({
                              target: {
                                value: {
                                  'x-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'x-coordinate'
                                    ],
                                  'y-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'y-coordinate'
                                    ],
                                  width: values?.customCoordinatesOb?.width,
                                  height: event?.target?.value,
                                },
                                name: 'customCoordinatesOb',
                              },
                            })
                          }}
                          width="90%"
                        />
                        <FormErrorMessage>
                          {getIn(form.errors, 'customCoordinatesOb.height')}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="customCoordinatesOb.width">
                    {({
                      field,
                      form,
                    }: FieldProps<
                      string,
                      { ['customCoordinatesOb.width']: number }
                    >) => (
                      <FormControl
                        isInvalid={Boolean(
                          getIn(form.errors, 'customCoordinatesOb.width') &&
                            getIn(form.touched, 'customCoordinatesOb.width'),
                        )}
                        isRequired={checkCfRequired(
                          values?.customCoordinatesOb,
                        )}
                      >
                        <FormLabel htmlFor="customCoordinatesOb.width">
                          {t('width', 'Width')}
                        </FormLabel>

                        <Input
                          {...field}
                          id="customCoordinatesOb.width"
                          name="customCoordinatesOb.width"
                          placeholder="e.g. 100"
                          value={values?.customCoordinatesOb?.['width']}
                          marginTop="5px"
                          onChange={event => {
                            handleChange({
                              target: {
                                value: {
                                  'x-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'x-coordinate'
                                    ],
                                  'y-coordinate':
                                    values?.customCoordinatesOb?.[
                                      'y-coordinate'
                                    ],
                                  width: event?.target?.value,
                                  height: values?.customCoordinatesOb?.height,
                                },
                                name: 'customCoordinatesOb',
                              },
                            })
                          }}
                          width="90%"
                        />
                        <FormErrorMessage>
                          {getIn(form.errors, 'customCoordinatesOb.width')}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>

                <FormErrorMessage>
                  {form.errors?.['customCoordinatesOb.width']}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
        ) : (
          <></>
        )}

        <Field name="webhookUrl">
          {({ field, form }: FieldProps<string, { webhookUrl: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.webhookUrl && form.touched.webhookUrl,
              )}
            >
              <FormLabel htmlFor="webhookUrl" fontWeight={700}>
                {t('webhook_url', 'Webhook URL')}
              </FormLabel>

              <Input
                {...field}
                id="webhookUrl"
                placeholder="e.g. https://example.com/ik-webhook"
                value={values?.webhookUrl}
                marginTop="5px"
                onChange={handleChange}
                width="60%"
              />
              <FormErrorMessage>{form.errors.webhookUrl}</FormErrorMessage>
            </FormControl>
          )}
        </Field>

        <FormLabel fontWeight={700}>
          {t('other_operations', 'Other operations')}
        </FormLabel>

        {!fileVersion && (
          <Field name="useUniqueFileName">
            {({
              field,
              form,
            }: FieldProps<string, { useUniqueFileName: string }>) => (
              <FormControl
                marginTop="1em"
                isInvalid={Boolean(
                  form.errors.useUniqueFileName &&
                    form.touched.useUniqueFileName,
                )}
              >
                <HStack spacing={0}>
                  <Checkbox
                    isChecked={values?.useUniqueFileName}
                    name="useUniqueFileName"
                    onChange={event => {
                      handleChange(event)
                    }}
                    mr="0.5rem"
                  >
                    {t('generate_unique_filenames_with_random_suffixes')}
                  </Checkbox>
                  <Popover trigger="hover" placement="right" strategy="fixed">
                    <PopoverTrigger>
                      <Flex>
                        <IconButton
                          aria-label="Info icon"
                          icon={<BsInfoCircleFill />}
                          color={colors.appBlueyGrey}
                          size="sm"
                          variant="link"
                        />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent width="md">
                      <PopoverArrow />
                      <PopoverBody>
                        <Text lineHeight={'1.5rem'} fontSize="sm">
                          {t(
                            'generate_unique_filenames_with_random_suffixes_description',
                          )}
                        </Text>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </HStack>
                <FormErrorMessage>
                  {form.errors.useUniqueFileName}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
        )}

        {!fileVersion && (
          <Field name="overwriteFile">
            {({ form }: FieldProps<string, { overwriteFile: string }>) => (
              <FormControl
                marginTop="1em"
                isInvalid={Boolean(
                  form.errors.overwriteFile && form.touched.overwriteFile,
                )}
              >
                <HStack spacing={0}>
                  <Checkbox
                    isChecked={values?.overwriteFile}
                    name="overwriteFile"
                    onChange={event => {
                      handleChange(event)
                    }}
                    mr="0.5rem"
                  >
                    {t('overwrite_files_during_upload')}
                  </Checkbox>
                  <Popover trigger="hover" placement="right" strategy="fixed">
                    <PopoverTrigger>
                      <Flex>
                        <IconButton
                          aria-label="Info icon"
                          icon={<BsInfoCircleFill />}
                          color={colors.appBlueyGrey}
                          size="sm"
                          variant="link"
                        />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent width="md">
                      <PopoverArrow />
                      <PopoverBody>
                        <Text lineHeight={'1.5rem'} fontSize="sm">
                          {t('overwrite_files_during_upload_description')}
                        </Text>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </HStack>
                <FormErrorMessage>{form.errors.overwriteFile}</FormErrorMessage>
              </FormControl>
            )}
          </Field>
        )}

        <Field name="isPrivateFile">
          {({ form }: FieldProps<string, { isPrivateFile: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.isPrivateFile && form.touched.isPrivateFile,
              )}
            >
              <HStack spacing={0}>
                <Checkbox
                  isChecked={values?.isPrivateFile}
                  name="isPrivateFile"
                  onChange={event => {
                    handleChange(event)
                  }}
                  mr="0.5rem"
                >
                  {t('private_files_during_upload')}
                </Checkbox>
                <Popover trigger="hover" placement="right" strategy="fixed">
                  <PopoverTrigger>
                    <Flex>
                      <IconButton
                        aria-label="Info icon"
                        icon={<BsInfoCircleFill />}
                        color={colors.appBlueyGrey}
                        size="sm"
                        variant="link"
                      />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent width="md">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text lineHeight={'1.5rem'} fontSize="sm">
                        {t('private_files_during_upload_description')}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <FormErrorMessage>{form.errors.isPrivateFile}</FormErrorMessage>
            </FormControl>
          )}
        </Field>

        <Field name="isPublished">
          {({ form }: FieldProps<string, { isPublished: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.isPublished && form.touched.isPublished,
              )}
            >
              <HStack spacing={0}>
                <Checkbox
                  isDisabled={
                    !user?.pricingPlan.featuresList.includes(
                      FeaturesEnum.DRAFT_ASSETS,
                    ) ?? true
                  }
                  isChecked={values?.isPublished}
                  name="isPublished"
                  onChange={event => {
                    handleChange(event)
                  }}
                  mr="0.5rem"
                >
                  {t('ensure_published_files_on_upload')}
                </Checkbox>
                <Popover trigger="hover" placement="right">
                  <PopoverTrigger>
                    <Flex mb={2}>
                      <IconButton
                        aria-label="Info icon"
                        icon={<BsInfoCircleFill />}
                        color={colors.appBlueyGrey}
                        size="sm"
                        variant="link"
                      />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent width="md">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text>
                        {user?.pricingPlan.featuresList.includes(
                          FeaturesEnum.DRAFT_ASSETS,
                        ) ? (
                          t('ensure_published_files_on_upload_description')
                        ) : (
                          <Trans t={t} i18nKey="draft_asset_plan_info">
                            <Text>
                              Draft assets feature is not available in your
                              current plan.{' '}
                              <Link
                                isExternal
                                lineHeight={1.2}
                                href="https://docs.imagekit.io/features/user-access-management#media-library-restricted-access"
                              >
                                {t('learn_more', 'Learn more')}
                              </Link>
                              .
                              <br />
                              <Link
                                isExternal
                                href="https://imagekit.io/contact-us/"
                              >
                                Contact sales
                              </Link>{' '}
                              to enable this feature.
                            </Text>
                          </Trans>
                        )}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <FormErrorMessage>{form.errors.isPublished}</FormErrorMessage>
            </FormControl>
          )}
        </Field>

        <Field name="overwriteAITags">
          {({ form }: FieldProps<string, { overwriteAITags: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.overwriteAITags && form.touched.overwriteAITags,
              )}
            >
              <HStack spacing={0}>
                <Checkbox
                  isChecked={values?.overwriteAITags}
                  name="overwriteAITags"
                  onChange={event => {
                    handleChange(event)
                  }}
                  mr="0.5rem"
                >
                  {t('overwrite_ai_tags_during_upload')}
                </Checkbox>
                <Popover trigger="hover" placement="right" strategy="fixed">
                  <PopoverTrigger>
                    <Flex>
                      <IconButton
                        aria-label="Info icon"
                        icon={<BsInfoCircleFill />}
                        color={colors.appBlueyGrey}
                        size="sm"
                        variant="link"
                      />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent width="md">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text lineHeight={'1.5rem'} fontSize="sm">
                        {t('overwrite_ai_tag_during_upload_description')}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <FormErrorMessage>{form.errors.overwriteAITags}</FormErrorMessage>
            </FormControl>
          )}
        </Field>

        <Field name="overwriteTags">
          {({ form }: FieldProps<string, { overwriteTags: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.overwriteTags && form.touched.overwriteTags,
              )}
            >
              <HStack spacing={0}>
                <Checkbox
                  isChecked={values?.overwriteTags}
                  name="overwriteTags"
                  onChange={event => {
                    handleChange(event)
                  }}
                  mr="0.5rem"
                >
                  {t('overwrite_tags_during_upload')}
                </Checkbox>
                <Popover trigger="hover" placement="right" strategy="fixed">
                  <PopoverTrigger>
                    <Flex>
                      <IconButton
                        aria-label="Info icon"
                        icon={<BsInfoCircleFill />}
                        color={colors.appBlueyGrey}
                        size="sm"
                        variant="link"
                      />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent width="md">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text lineHeight={'1.5rem'} fontSize="sm">
                        {t('overwrite_tag_during_upload_description')}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <FormErrorMessage>{form.errors.overwriteTags}</FormErrorMessage>
            </FormControl>
          )}
        </Field>

        <Field name="overwriteDescription">
          {({ form }: FieldProps<string, { overwriteDescription: string }>) => (
            <FormControl
              marginTop="1em"
              isInvalid={Boolean(
                form.errors.overwriteDescription &&
                  form.touched.overwriteDescription,
              )}
            >
              <HStack spacing={0}>
                <Checkbox
                  isChecked={values?.overwriteDescription}
                  name="overwriteDescription"
                  onChange={event => {
                    handleChange(event)
                  }}
                  mr="0.5rem"
                >
                  {t('overwrite_description_during_upload')}
                </Checkbox>
                <Popover trigger="hover" placement="right" strategy="fixed">
                  <PopoverTrigger>
                    <Flex>
                      <IconButton
                        aria-label="Info icon"
                        icon={<BsInfoCircleFill />}
                        color={colors.appBlueyGrey}
                        size="sm"
                        variant="link"
                      />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent width="md">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text lineHeight={'1.5rem'} fontSize="sm">
                        {t('overwrite_description_during_upload_description')}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <FormErrorMessage>
                {form.errors.overwriteDescription}
              </FormErrorMessage>
            </FormControl>
          )}
        </Field>

        {isCommonSettings ||
        (!isCommonSettings &&
          file?.type &&
          (fileConstraints.allowedFiles.image.includes(file.type) ||
            fileConstraints.allowedFiles.video.includes(file.type))) ? (
          <>
            <Field name="preTransformation">
              {({
                field,
                form,
              }: FieldProps<string, { preTransformation: string }>) => (
                <FormControl
                  isInvalid={Boolean(
                    form.errors.preTransformation &&
                      form.touched.preTransformation,
                  )}
                >
                  <HStack spacing={0} mb={3}>
                    <FormLabel
                      htmlFor="preTransformation"
                      fontWeight={700}
                      mb={0}
                    >
                      {t('pre_transformation', 'Pre transformation')}
                    </FormLabel>
                    <Popover trigger="hover" placement="right" strategy="fixed">
                      <PopoverTrigger>
                        <Flex>
                          <IconButton
                            aria-label="Info icon"
                            icon={<BsInfoCircleFill />}
                            color={colors.appBlueyGrey}
                            size="sm"
                            variant="link"
                          />
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent width="md">
                        <PopoverArrow />
                        <PopoverBody>
                          <Text lineHeight={'1.5rem'} fontSize="sm">
                            {t('pre_transformation_info')}
                          </Text>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>

                  <Input
                    {...field}
                    id="preTransformation"
                    placeholder="w-40,h-30"
                    value={values?.preTransformation}
                    width="60%"
                    onChange={event => {
                      handleChange(event)
                    }}
                  />
                  <FormErrorMessage>
                    {form.errors.preTransformation}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="postTransformation">
              {({ form }) => (
                <FormControl isInvalid={form.errors.postTransformation}>
                  <HStack spacing={0} mb={3}>
                    <FormLabel
                      htmlFor="postTransformation"
                      fontWeight={700}
                      mb={0}
                    >
                      {t('post_transformation', 'Post transformation')}
                    </FormLabel>
                    <Popover trigger="hover" placement="right" strategy="fixed">
                      <PopoverTrigger>
                        <Flex>
                          <IconButton
                            aria-label="Info icon"
                            icon={<BsInfoCircleFill />}
                            color={colors.appBlueyGrey}
                            size="sm"
                            variant="link"
                          />
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent width="md">
                        <PopoverArrow />
                        <PopoverBody>
                          <Text lineHeight={'1.5rem'} fontSize="sm">
                            {t('post_transformation_info')}
                          </Text>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                  <PostTransformationFields
                    {...{
                      form,
                      postTransformation: form?.values?.postTransformation,
                      isDefault: false,
                      isCommonSettings,
                      type: isCommonSettings ? null : file?.type,
                    }}
                  />
                </FormControl>
              )}
            </Field>
          </>
        ) : (
          <></>
        )}

        {!pathPolicy && (
          <Field name="overwriteCustomMetadata">
            {({
              form,
            }: FieldProps<string, { overwriteCustomMetadata: string }>) => (
              <FormControl
                marginTop="1em"
                isInvalid={Boolean(
                  form.errors.overwriteCustomMetadata &&
                    form.touched.overwriteCustomMetadata,
                )}
              >
                <FormLabel
                  htmlFor="customMetadataSelect"
                  fontWeight={700}
                  mb={'16px'}
                >
                  {t('custom_metadata', 'Custom Metadata')}
                </FormLabel>
                <HStack spacing={0}>
                  <Checkbox
                    isChecked={values?.overwriteCustomMetadata}
                    name="overwriteCustomMetadata"
                    onChange={event => {
                      handleChange(event)
                      setDisableCmSelect(!Boolean(event.target.checked))
                    }}
                    mr="0.5rem"
                  >
                    {t('overwrite_custom_metadata_during_upload')}
                  </Checkbox>
                  <Popover trigger="hover" placement="right" strategy="fixed">
                    <PopoverTrigger>
                      <Flex>
                        <IconButton
                          aria-label="Info icon"
                          icon={<BsInfoCircleFill />}
                          color={colors.appBlueyGrey}
                          size="sm"
                          variant="link"
                        />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent width="md">
                      <PopoverArrow />
                      <PopoverBody>
                        <Text lineHeight={'1.5rem'} fontSize="sm">
                          {t('custom_metadata_during_upload_description')}
                        </Text>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </HStack>
                <FormErrorMessage>
                  {form.errors.overwriteCustomMetadata}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
        )}

        {!pathPolicy && (
          <Alert status="info" style={{ maxWidth: '320px' }}>
            <AlertIcon
              height="1em"
              width="1em"
              alignItems="flex-start"
              marginTop="4px"
            />
            <AlertDescription fontSize="sm">
              {t(
                'custom_metadata_default_alert',
                'Default values associated with custom metadata fields will\
             persist even after the fields are cleared via the form.',
              )}
            </AlertDescription>
          </Alert>
        )}

        {pathPolicy && (
          <Alert status="info" maxW="320px">
            <AlertIcon h="1em" w="1em" mt="4px" alignItems="flex-start" />
            <AlertDescription fontSize="sm">
              Path policy "{pathPolicy.name}" is enforced here.&nbsp;
              <Link
                as="a"
                lineHeight="1.2"
                href={`/dashboard/path-policy/${pathPolicy.pathPolicyId}`}
                target="_blank"
                rel="noopener noreferrer"
                color="appClearBlue"
              >
                Click here
              </Link>{' '}
              to view.
            </AlertDescription>
          </Alert>
        )}

        <Field name="customMetadataSelect">
          {({ form }: FieldProps<string, { customMetadataSelect: string }>) => (
            <FormControl
              style={{ maxWidth: '350px' }}
              marginTop="1em"
              isDisabled={disableCmSelect}
              isInvalid={Boolean(
                form.errors.customMetadataSelect &&
                  form.touched.customMetadataSelect,
              )}
            >
              <Select
                name="customMetadataSelect"
                value={values?.customMetadataSelect}
                isDisabled={Boolean(pathPolicy) || disableCmSelect}
                onChange={event => {
                  handleChange(event)
                  setShowCmForm(Boolean(event.target.value === 'cm_form'))
                }}
              >
                <option value="cm_default">
                  {' '}
                  {t('set_default_values', 'Set default values')}
                </option>
                <option value="cm_form">
                  {t('show_metadata_form', 'Set custom metadata')}
                </option>
              </Select>

              <FormErrorMessage>
                {form.errors.customMetadataSelect}
              </FormErrorMessage>
            </FormControl>
          )}
        </Field>

        {showCmForm ? (
          <CMUploadForm
            cmFields={cmFields}
            disableCMField={disableCmSelect}
            renderFromHeader={false}
          />
        ) : (
          <></>
        )}
      </VStack>
    </VStack>
  )
}

export default MetaDataFields
